import * as XLSX from 'xlsx';

import { cellText } from '@/lib/daily-reports/cells';
import { readFileArrayBuffer } from '@/lib/daily-reports/readFileArrayBuffer';
import { mergeById } from '@/lib/mergeById';

import { isBlank } from './cells';
import type { ParsedSheet, ParseWorkbookResult } from './types';

function sheetRows(workbook: XLSX.WorkBook, name: string): unknown[][] {
  const sheet = workbook.Sheets[name];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
}

function rowCells(row: unknown[], width: number): string[] {
  const cells: string[] = [];
  for (let index = 0; index < width; index += 1) {
    cells.push(cellText(row[index]));
  }
  return cells;
}

function parseSheet(name: string, rows: unknown[][]): ParsedSheet | null {
  const headerRow = rows[0];
  if (!headerRow) return null;
  const headers = headerRow.map((cell) => cellText(cell));
  if (headers.every(isBlank)) return null;
  return {
    name,
    headers,
    rows: rows.slice(1).map((row) => rowCells(row, headers.length)),
  };
}

export function parsePriceListWorkbook(
  workbook: XLSX.WorkBook,
  fileName: string,
  id: string = crypto.randomUUID(),
): ParseWorkbookResult {
  const sheets: ParsedSheet[] = [];
  for (const name of workbook.SheetNames) {
    const parsed = parseSheet(name, sheetRows(workbook, name));
    if (parsed) sheets.push(parsed);
  }
  if (sheets.length === 0) {
    return { status: 'empty', id, fileName };
  }
  return { status: 'parsed', id, fileName, workbook: { fileName, sheets } };
}

export async function parsePriceListFile(
  file: File,
): Promise<ParseWorkbookResult> {
  const id = crypto.randomUUID();
  try {
    const workbook = XLSX.read(await readFileArrayBuffer(file), {
      type: 'array',
    });
    return parsePriceListWorkbook(workbook, file.name, id);
  } catch {
    return { status: 'unreadable', id, fileName: file.name };
  }
}

export function uniqueHeaders(
  results: readonly ParseWorkbookResult[],
): string[] {
  const seen = new Set<string>();
  const headers: string[] = [];
  for (const result of results) {
    if (result.status !== 'parsed') continue;
    for (const sheet of result.workbook.sheets) {
      for (const header of sheet.headers) {
        if (isBlank(header) || seen.has(header)) continue;
        seen.add(header);
        headers.push(header);
      }
    }
  }
  return headers;
}

export function mergeParseResults(
  current: readonly ParseWorkbookResult[],
  incoming: readonly ParseWorkbookResult[],
): ParseWorkbookResult[] {
  return mergeById(current, incoming);
}
